
import React from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";

const articlesContent: Record<number, { ar: { title: string; content: string[] }; en: { title: string; content: string[] } }> = {
  1: {
    ar: {
      title: "أحدث التقنيات في صناعة الألمنيوم",
      content: [
        "شهدت صناعة الألمنيوم تطوراً هائلاً في السنوات الأخيرة، حيث أصبحت التقنيات الحديثة تُمكّن من إنتاج منتجات أكثر دقة وجودة وكفاءة. في هذا المقال، نستعرض أبرز التقنيات المتقدمة في هذا المجال.",
        
        "## تقنيات القطع بالليزر",
        "تُعد تقنية القطع بالليزر من أهم الابتكارات في صناعة الألمنيوم الحديثة. تتميز هذه التقنية بـ:",
        "- **دقة متناهية**: قطع بدقة تصل إلى 0.1 ملم",
        "- **سرعة عالية**: إنتاجية أعلى مع جودة ثابتة",
        "- **حواف نظيفة**: لا حاجة لمعالجة إضافية للحواف",
        "- **تصاميم معقدة**: إمكانية تنفيذ أشكال وزخارف دقيقة",
        
        "## أنظمة CNC المتقدمة",
        "تستخدم ماكينات التحكم الرقمي (CNC) في تشكيل الألمنيوم بدقة عالية:",
        "- برمجة ثلاثية الأبعاد للتصاميم المعقدة",
        "- تكرار دقيق للقطع المتماثلة",
        "- تقليل الهدر في المواد الخام",
        "- مرونة في تغيير التصاميم",
        
        "## تقنيات اللحام المتطورة",
        "### لحام TIG",
        "يوفر لحاماً نظيفاً وقوياً مناسباً للألمنيوم الرقيق والتطبيقات الدقيقة.",
        
        "### لحام MIG",
        "أسرع وأكثر كفاءة للمشاريع الكبيرة والإنتاج الصناعي.",
        
        "### لحام الاحتكاك",
        "تقنية حديثة تستخدم الحرارة الناتجة عن الاحتكاك لربط القطع بدون مواد إضافية.",
        
        "## المعالجة السطحية",
        "### الأنودة (Anodizing)",
        "عملية كهروكيميائية تُكوّن طبقة أكسيد واقية على سطح الألمنيوم:",
        "- حماية من التآكل والخدوش",
        "- إمكانية التلوين بألوان متعددة",
        "- زيادة صلابة السطح",
        
        "### الطلاء بالمسحوق",
        "تقنية صديقة للبيئة توفر تشطيباً متيناً وجمالياً:",
        "- مقاومة للأشعة فوق البنفسجية",
        "- تنوع كبير في الألوان والملمس",
        "- متانة عالية",
        
        "## أنظمة العزل الحراري",
        "تستخدم تقنية الكسر الحراري (Thermal Break) في نوافذ وأبواب الألمنيوم:",
        "- فصل بين الإطار الداخلي والخارجي",
        "- تقليل انتقال الحرارة بنسبة تصل إلى 60%",
        "- توفير في استهلاك الطاقة",
        "- تحسين العزل الصوتي",
        
        "## في مجموعة الفنون",
        "نفخر في مجموعة الفنون بامتلاك أحدث التقنيات والمعدات في صناعة الألمنيوم. فريقنا من المهندسين والفنيين المتخصصين يعمل على تقديم منتجات تجمع بين الجودة العالية والتصميم العصري."
      ]
    },
    en: {
      title: "Latest Technologies in Aluminum Manufacturing",
      content: [
        "The aluminum industry has witnessed tremendous development in recent years, with modern technologies enabling the production of more precise, high-quality, and efficient products. In this article, we explore the most prominent advanced technologies in this field.",
        
        "## Laser Cutting Technologies",
        "Laser cutting technology is one of the most important innovations in modern aluminum manufacturing. This technology features:",
        "- **Extreme precision**: Cutting accuracy up to 0.1 mm",
        "- **High speed**: Higher productivity with consistent quality",
        "- **Clean edges**: No need for additional edge processing",
        "- **Complex designs**: Ability to execute intricate shapes and patterns",
        
        "## Advanced CNC Systems",
        "Computer Numerical Control (CNC) machines are used to shape aluminum with high precision:",
        "- 3D programming for complex designs",
        "- Precise repetition of identical parts",
        "- Reduced raw material waste",
        "- Flexibility in design changes",
        
        "## Advanced Welding Technologies",
        "### TIG Welding",
        "Provides clean and strong welding suitable for thin aluminum and precise applications.",
        
        "### MIG Welding",
        "Faster and more efficient for large projects and industrial production.",
        
        "### Friction Welding",
        "A modern technique using heat generated by friction to join parts without additional materials.",
        
        "## Surface Treatment",
        "### Anodizing",
        "An electrochemical process that forms a protective oxide layer on the aluminum surface:",
        "- Protection from corrosion and scratches",
        "- Ability to color in multiple shades",
        "- Increased surface hardness",
        
        "### Powder Coating",
        "An environmentally friendly technology providing durable and aesthetic finishes:",
        "- UV resistance",
        "- Wide variety of colors and textures",
        "- High durability",
        
        "## Thermal Insulation Systems",
        "Thermal break technology is used in aluminum windows and doors:",
        "- Separation between inner and outer frames",
        "- Heat transfer reduction up to 60%",
        "- Energy consumption savings",
        "- Improved sound insulation",
        
        "## At ALFUNOON Group",
        "At ALFUNOON Group, we are proud to own the latest technologies and equipment in aluminum manufacturing. Our team of specialized engineers and technicians works to deliver products that combine high quality with modern design."
      ]
    }
  },
  2: {
    ar: {
      title: "معرض البناء السعودي 2023",
      content: [
        "يُعد معرض البناء السعودي من أكبر وأهم الفعاليات في قطاع البناء والتشييد في المنطقة، حيث يجمع كبار الشركات والمصنعين والمبتكرين تحت سقف واحد.",
        
        "## نظرة عامة على المعرض",
        "أُقيم معرض البناء السعودي 2023 في مركز الرياض الدولي للمؤتمرات والمعارض، واستقطب أكثر من 500 عارض من 30 دولة، مع حضور يتجاوز 25,000 زائر متخصص.",
        
        "## أبرز المشاركين",
        "شهد المعرض مشاركة واسعة من:",
        "- شركات تصنيع مواد البناء المحلية والعالمية",
        "- مطورون عقاريون رائدون",
        "- شركات التصميم المعماري",
        "- موردو التقنيات الذكية للمباني",
        "- مصنعو الألمنيوم والحديد والزجاج",
        
        "## الابتكارات المعروضة",
        "### المباني الذكية",
        "عُرضت أحدث تقنيات المباني الذكية التي تشمل:",
        "- أنظمة التحكم الآلي بالإضاءة والتكييف",
        "- حلول إدارة الطاقة الذكية",
        "- أنظمة الأمان المتكاملة",
        
        "### مواد البناء المستدامة",
        "ركز المعرض على الاستدامة من خلال عرض:",
        "- مواد بناء صديقة للبيئة",
        "- أنظمة العزل الحراري المتقدمة",
        "- حلول الطاقة المتجددة",
        
        "### تقنيات التصنيع الحديثة",
        "شهد الزوار عروضاً حية لـ:",
        "- الطباعة ثلاثية الأبعاد للمباني",
        "- الروبوتات في البناء",
        "- تقنيات BIM للتصميم والإدارة",
        
        "## مشاركة مجموعة الفنون",
        "شاركت مجموعة الفنون بجناح متميز عرضت فيه أحدث منتجاتها في مجالات:",
        "- تصنيع الألمنيوم والواجهات",
        "- أعمال الحديد والستانلس ستيل",
        "- الزجاج المعالج حرارياً",
        "- الأخشاب والرخام",
        
        "## أهمية المعرض للقطاع",
        "يُساهم المعرض في:",
        "- تبادل الخبرات والمعرفة",
        "- بناء شراكات تجارية جديدة",
        "- الاطلاع على أحدث الابتكارات",
        "- دعم رؤية المملكة 2030 في قطاع البناء"
      ]
    },
    en: {
      title: "Saudi Build Exhibition 2023",
      content: [
        "The Saudi Build Exhibition is one of the largest and most important events in the construction sector in the region, bringing together major companies, manufacturers, and innovators under one roof.",
        
        "## Exhibition Overview",
        "Saudi Build 2023 was held at the Riyadh International Convention and Exhibition Center, attracting over 500 exhibitors from 30 countries, with attendance exceeding 25,000 specialized visitors.",
        
        "## Key Participants",
        "The exhibition witnessed wide participation from:",
        "- Local and international building materials manufacturers",
        "- Leading real estate developers",
        "- Architectural design firms",
        "- Smart building technology suppliers",
        "- Aluminum, iron, and glass manufacturers",
        
        "## Innovations Showcased",
        "### Smart Buildings",
        "The latest smart building technologies were displayed, including:",
        "- Automated lighting and air conditioning control systems",
        "- Smart energy management solutions",
        "- Integrated security systems",
        
        "### Sustainable Building Materials",
        "The exhibition focused on sustainability by showcasing:",
        "- Eco-friendly building materials",
        "- Advanced thermal insulation systems",
        "- Renewable energy solutions",
        
        "### Modern Manufacturing Technologies",
        "Visitors witnessed live demonstrations of:",
        "- 3D printing for buildings",
        "- Robotics in construction",
        "- BIM technologies for design and management",
        
        "## ALFUNOON Group Participation",
        "ALFUNOON Group participated with a distinguished pavilion showcasing its latest products in:",
        "- Aluminum manufacturing and facades",
        "- Iron and stainless steel works",
        "- Heat-treated glass",
        "- Wood and marble",
        
        "## Exhibition Importance for the Sector",
        "The exhibition contributes to:",
        "- Exchange of expertise and knowledge",
        "- Building new business partnerships",
        "- Learning about the latest innovations",
        "- Supporting Saudi Vision 2030 in the construction sector"
      ]
    }
  },
  3: {
    ar: {
      title: "مقاومة الحديد للتآكل والصدأ",
      content: [
        "يُعتبر الحديد من أكثر المعادن استخداماً في العالم، إلا أن تعرضه للتآكل والصدأ يُشكل تحدياً كبيراً يؤثر على متانته وعمره الافتراضي. في هذا المقال، نستعرض أحدث التقنيات والطرق المستخدمة لحماية الحديد من التآكل.",
        
        "## ما هو التآكل والصدأ؟",
        "التآكل هو عملية كيميائية تحدث عندما يتفاعل الحديد مع الأكسجين والرطوبة في البيئة المحيطة. ينتج عن هذا التفاعل أكسيد الحديد المائي، المعروف بالصدأ، الذي يُضعف البنية المعدنية ويُقلل من قوتها.",
        
        "## العوامل المؤثرة في تآكل الحديد",
        "### 1. الرطوبة",
        "تُعد الرطوبة العامل الأساسي في حدوث الصدأ، حيث يحتاج التفاعل الكيميائي إلى وجود الماء كوسيط.",
        
        "### 2. الأكسجين",
        "يتفاعل الأكسجين مع الحديد في وجود الرطوبة لتكوين أكسيد الحديد (الصدأ).",
        
        "### 3. الملوحة",
        "تُسرّع الأملاح عملية التآكل بشكل كبير، خاصة في المناطق الساحلية والبيئات الصناعية.",
        
        "### 4. درجة الحرارة",
        "ترتفع سرعة التآكل مع ارتفاع درجات الحرارة.",
        
        "## طرق حماية الحديد من التآكل",
        "### 1. الطلاء الواقي",
        "- **الدهانات المضادة للصدأ**: طبقات من الدهان تمنع وصول الرطوبة والأكسجين",
        "- **الطلاء بالمسحوق (Powder Coating)**: طبقة بوليمر متينة توفر حماية طويلة الأمد",
        "- **الطلاء الإيبوكسي**: مقاومة عالية للمواد الكيميائية والرطوبة",
        
        "### 2. الجلفنة (Galvanizing)",
        "عملية تغطية الحديد بطبقة من الزنك تعمل كحاجز واقٍ. حتى عند تعرض السطح للخدش، يتآكل الزنك بدلاً من الحديد (الحماية الكاثودية).",
        
        "### 3. الطلاء الكهربائي",
        "ترسيب طبقة رقيقة من معدن آخر (مثل الكروم أو النيكل) على سطح الحديد باستخدام التيار الكهربائي.",
        
        "### 4. سبائك الحديد المقاومة للصدأ",
        "- **الستانلس ستيل**: إضافة الكروم والنيكل للحديد لتكوين طبقة أكسيد واقية",
        "- **حديد الكورتن (Corten Steel)**: يُكوّن طبقة صدأ واقية تمنع التآكل العميق",
        
        "### 5. الحماية الكاثودية",
        "استخدام معدن أكثر نشاطاً (مثل الزنك أو المغنيسيوم) يتآكل بدلاً من الحديد.",
        
        "## تقنيات حديثة في مقاومة التآكل",
        "- **النانو تكنولوجي**: طلاءات نانوية توفر حماية فائقة بسماكة ضئيلة",
        "- **الطلاءات الذكية**: تتفاعل مع البيئة وتُصلح نفسها عند التلف",
        "- **المعالجة الحرارية**: تحسين البنية البلورية للحديد لزيادة مقاومته",
        
        "## الصيانة الدورية",
        "1. **الفحص المنتظم**: الكشف المبكر عن علامات التآكل",
        "2. **التنظيف**: إزالة الأوساخ والملوثات التي تُسرّع التآكل",
        "3. **إعادة الطلاء**: تجديد الطبقات الواقية عند الحاجة",
        "4. **معالجة الخدوش**: إصلاح أي تلف في الطبقة الواقية فوراً",
        
        "## خدمات مجموعة الفنون",
        "في مجموعة الفنون للصناعات، نقدم حلولاً متكاملة لحماية منتجاتنا الحديدية من التآكل:",
        "- استخدام أجود أنواع الحديد والسبائك المقاومة للصدأ",
        "- تطبيق أحدث تقنيات الجلفنة والطلاء الواقي",
        "- ضمان شامل على جميع منتجاتنا الحديدية",
        "- خدمات صيانة دورية للحفاظ على جودة المنتجات",
        
        "## الخلاصة",
        "حماية الحديد من التآكل ليست رفاهية بل ضرورة للحفاظ على الاستثمار وضمان السلامة. اختيار الطريقة المناسبة يعتمد على بيئة الاستخدام والميزانية والعمر الافتراضي المطلوب. في مجموعة الفنون، نساعدكم في اختيار أفضل الحلول لمشاريعكم."
      ]
    },
    en: {
      title: "Iron Corrosion and Rust Resistance",
      content: [
        "Iron is one of the most widely used metals in the world, yet its susceptibility to corrosion and rust poses a significant challenge affecting its durability and lifespan. In this article, we explore the latest techniques and methods used to protect iron from corrosion.",
        
        "## What is Corrosion and Rust?",
        "Corrosion is a chemical process that occurs when iron reacts with oxygen and moisture in the surrounding environment. This reaction produces hydrated iron oxide, known as rust, which weakens the metal structure and reduces its strength.",
        
        "## Factors Affecting Iron Corrosion",
        "### 1. Moisture",
        "Moisture is the primary factor in rust formation, as the chemical reaction requires water as a medium.",
        
        "### 2. Oxygen",
        "Oxygen reacts with iron in the presence of moisture to form iron oxide (rust).",
        
        "### 3. Salinity",
        "Salts significantly accelerate the corrosion process, especially in coastal areas and industrial environments.",
        
        "### 4. Temperature",
        "Corrosion rate increases with rising temperatures.",
        
        "## Methods to Protect Iron from Corrosion",
        "### 1. Protective Coatings",
        "- **Anti-rust paints**: Paint layers that prevent moisture and oxygen from reaching the metal",
        "- **Powder Coating**: A durable polymer layer providing long-term protection",
        "- **Epoxy Coating**: High resistance to chemicals and moisture",
        
        "### 2. Galvanizing",
        "The process of covering iron with a layer of zinc that acts as a protective barrier. Even when the surface is scratched, zinc corrodes instead of iron (cathodic protection).",
        
        "### 3. Electroplating",
        "Depositing a thin layer of another metal (such as chrome or nickel) on the iron surface using electric current.",
        
        "### 4. Rust-Resistant Iron Alloys",
        "- **Stainless Steel**: Adding chromium and nickel to iron to form a protective oxide layer",
        "- **Corten Steel**: Forms a protective rust layer that prevents deep corrosion",
        
        "### 5. Cathodic Protection",
        "Using a more active metal (such as zinc or magnesium) that corrodes instead of iron.",
        
        "## Modern Anti-Corrosion Technologies",
        "- **Nanotechnology**: Nano coatings providing superior protection with minimal thickness",
        "- **Smart Coatings**: React with the environment and self-repair when damaged",
        "- **Heat Treatment**: Improving iron's crystalline structure to increase resistance",
        
        "## Regular Maintenance",
        "1. **Regular Inspection**: Early detection of corrosion signs",
        "2. **Cleaning**: Removing dirt and pollutants that accelerate corrosion",
        "3. **Repainting**: Renewing protective layers when needed",
        "4. **Scratch Treatment**: Immediately repairing any damage to the protective layer",
        
        "## ALFUNOON Group Services",
        "At ALFUNOON Industries Group, we provide integrated solutions to protect our iron products from corrosion:",
        "- Using the finest quality iron and rust-resistant alloys",
        "- Applying the latest galvanizing and protective coating technologies",
        "- Comprehensive warranty on all our iron products",
        "- Regular maintenance services to maintain product quality",
        
        "## Conclusion",
        "Protecting iron from corrosion is not a luxury but a necessity to preserve investment and ensure safety. Choosing the right method depends on the usage environment, budget, and required lifespan. At ALFUNOON Group, we help you choose the best solutions for your projects."
      ]
    }
  },
  5: {
    ar: {
      title: "صناعة الزجاج المعالج حرارياً",
      content: [
        "يُعد الزجاج المعالج حرارياً من أهم المواد المستخدمة في البناء والتصميم المعماري الحديث، حيث يتميز بخصائص فريدة تجعله الخيار الأمثل للمشاريع التي تتطلب الأمان والمتانة والجمال في آن واحد.",
        
        "## ما هو الزجاج المعالج حرارياً؟",
        "الزجاج المعالج حرارياً هو زجاج تم تسخينه إلى درجات حرارة عالية جداً (حوالي 620 درجة مئوية) ثم تبريده بسرعة باستخدام تيارات هواء قوية. هذه العملية تُعرف بـ\"التقسية\" وتمنح الزجاج قوة أكبر بخمس مرات من الزجاج العادي.",
        
        "## أنواع الزجاج المعالج حرارياً",
        "### 1. الزجاج المُقسّى (Tempered Glass)",
        "يتميز بقوته العالية ومقاومته للصدمات. عند كسره، يتفتت إلى قطع صغيرة غير حادة، مما يقلل من خطر الإصابات. يُستخدم في واجهات المباني، والأبواب الزجاجية، والطاولات.",
        
        "### 2. الزجاج المُقوّى بالحرارة (Heat-Strengthened Glass)",
        "أقوى من الزجاج العادي بمرتين تقريباً، لكنه أقل قوة من الزجاج المُقسّى. يُستخدم عادةً في التطبيقات التي لا تتطلب مستوى عالٍ من الأمان.",
        
        "### 3. الزجاج العازل (Insulated Glass)",
        "يتكون من طبقتين أو أكثر من الزجاج المعالج حرارياً مع فراغ هوائي أو غاز معزول بينهما. يوفر عزلاً حرارياً وصوتياً ممتازاً.",
        
        "## مميزات الزجاج المعالج حرارياً",
        "- **الأمان**: عند الكسر يتفتت لقطع صغيرة غير مؤذية",
        "- **القوة**: أقوى بخمس مرات من الزجاج العادي",
        "- **مقاومة الحرارة**: يتحمل تغيرات درجات الحرارة الكبيرة",
        "- **المتانة**: مقاوم للخدوش والتآكل",
        "- **الشفافية**: يحافظ على وضوح الرؤية ونفاذية الضوء",
        
        "## تطبيقات الزجاج المعالج حرارياً",
        "يُستخدم الزجاج المعالج حرارياً في العديد من التطبيقات المعمارية والصناعية:",
        "- واجهات المباني التجارية والسكنية",
        "- النوافذ والأبواب الزجاجية",
        "- الفواصل والحواجز الزجاجية الداخلية",
        "- السلالم والدرابزينات الزجاجية",
        "- أسقف المناور والقباب الزجاجية",
        "- الأثاث الزجاجي كالطاولات والأرفف",
        
        "## عملية التصنيع في مجموعة الفنون",
        "في مجموعة الفنون للصناعات، نستخدم أحدث تقنيات المعالجة الحرارية للزجاج. تمر عملية التصنيع بعدة مراحل دقيقة:",
        "1. **القطع والتشكيل**: قطع الزجاج بالأبعاد المطلوبة بدقة متناهية",
        "2. **الصقل والتنظيف**: إزالة الحواف الحادة وتنظيف السطح",
        "3. **التسخين**: رفع درجة الحرارة تدريجياً حتى 620 درجة مئوية",
        "4. **التبريد السريع**: تبريد الزجاج بتيارات هوائية قوية ومتجانسة",
        "5. **الفحص والاختبار**: اختبارات الجودة للتأكد من مطابقة المواصفات",
        
        "## لماذا تختار مجموعة الفنون؟",
        "نتميز في مجموعة الفنون بخبرة تزيد عن 10 سنوات في صناعة الزجاج المعالج حرارياً. نقدم منتجات عالية الجودة بأسعار تنافسية، مع ضمان شامل وخدمة ما بعد البيع المتميزة. فريقنا من المهندسين والفنيين المتخصصين جاهز لتلبية احتياجاتكم وتحويل رؤيتكم إلى واقع."
      ]
    },
    en: {
      title: "Heat-Treated Glass Manufacturing",
      content: [
        "Heat-treated glass is one of the most important materials used in modern construction and architectural design, featuring unique properties that make it the ideal choice for projects requiring safety, durability, and beauty all at once.",
        
        "## What is Heat-Treated Glass?",
        "Heat-treated glass is glass that has been heated to very high temperatures (approximately 620°C) and then rapidly cooled using strong air currents. This process, known as \"tempering,\" gives the glass five times the strength of regular glass.",
        
        "## Types of Heat-Treated Glass",
        "### 1. Tempered Glass",
        "Known for its high strength and impact resistance. When broken, it shatters into small, blunt pieces, reducing the risk of injuries. Used in building facades, glass doors, and tables.",
        
        "### 2. Heat-Strengthened Glass",
        "Approximately twice as strong as regular glass, but less strong than tempered glass. Typically used in applications that don't require a high level of safety.",
        
        "### 3. Insulated Glass",
        "Consists of two or more layers of heat-treated glass with an air gap or insulating gas between them. Provides excellent thermal and acoustic insulation.",
        
        "## Advantages of Heat-Treated Glass",
        "- **Safety**: Breaks into small, harmless pieces",
        "- **Strength**: Five times stronger than regular glass",
        "- **Heat Resistance**: Withstands significant temperature changes",
        "- **Durability**: Resistant to scratches and corrosion",
        "- **Clarity**: Maintains visual clarity and light transmission",
        
        "## Applications of Heat-Treated Glass",
        "Heat-treated glass is used in many architectural and industrial applications:",
        "- Commercial and residential building facades",
        "- Glass windows and doors",
        "- Interior glass partitions and barriers",
        "- Glass stairs and railings",
        "- Skylight roofs and glass domes",
        "- Glass furniture such as tables and shelves",
        
        "## Manufacturing Process at ALFUNOON Group",
        "At ALFUNOON Industries Group, we use the latest glass heat treatment technologies. The manufacturing process goes through several precise stages:",
        "1. **Cutting and Shaping**: Cutting glass to required dimensions with extreme precision",
        "2. **Polishing and Cleaning**: Removing sharp edges and cleaning the surface",
        "3. **Heating**: Gradually raising the temperature to 620°C",
        "4. **Rapid Cooling**: Cooling the glass with strong, uniform air currents",
        "5. **Inspection and Testing**: Quality tests to ensure specification compliance",
        
        "## Why Choose ALFUNOON Group?",
        "At ALFUNOON Group, we have over 10 years of experience in heat-treated glass manufacturing. We offer high-quality products at competitive prices, with comprehensive warranty and excellent after-sales service. Our team of specialized engineers and technicians is ready to meet your needs and transform your vision into reality."
      ]
    }
  },
  4: {
    ar: {
      title: "ملتقى الفنون للصناعة الحديثة",
      content: [
        "أقامت مجموعة الفنون للصناعات ملتقاها السنوي الذي جمع نخبة من الخبراء والمتخصصين في مجالات التصنيع والتصميم، لمناقشة أحدث التوجهات والتحديات في القطاع.",
        
        "## أهداف الملتقى",
        "سعى الملتقى لتحقيق عدة أهداف استراتيجية:",
        "- تبادل الخبرات بين المتخصصين في الصناعة",
        "- استعراض أحدث التقنيات والابتكارات",
        "- بناء شراكات تجارية جديدة",
        "- مناقشة تحديات السوق وحلولها",
        "- دعم الكوادر الوطنية في القطاع الصناعي",
        
        "## المتحدثون الرئيسيون",
        "### خبراء التصنيع",
        "شارك في الملتقى نخبة من الخبراء المحليين والدوليين الذين قدموا رؤاهم حول:",
        "- مستقبل الصناعة التحويلية في المملكة",
        "- التحول الرقمي في قطاع التصنيع",
        "- معايير الجودة العالمية",
        
        "### رواد الأعمال",
        "قدم عدد من رواد الأعمال الناجحين قصصهم الملهمة وتجاربهم في:",
        "- بناء مشاريع صناعية ناجحة",
        "- التغلب على التحديات والعقبات",
        "- التوسع والنمو في الأسواق المحلية والإقليمية",
        
        "## ورش العمل",
        "تضمن الملتقى ورش عمل تفاعلية شملت:",
        "- **ورشة التصميم الحديث**: استعراض أحدث اتجاهات التصميم الداخلي",
        "- **ورشة إدارة الجودة**: تطبيق معايير ISO في المصانع",
        "- **ورشة التسويق الرقمي**: استراتيجيات التسويق للمنتجات الصناعية",
        "- **ورشة السلامة المهنية**: أفضل الممارسات في بيئة العمل",
        
        "## المعرض المصاحب",
        "أُقيم معرض مصاحب للملتقى عرضت فيه الشركات المشاركة:",
        "- أحدث منتجاتها وابتكاراتها",
        "- نماذج من المشاريع المنفذة",
        "- التقنيات والمعدات الحديثة",
        
        "## توصيات الملتقى",
        "خرج الملتقى بعدة توصيات مهمة:",
        "1. تعزيز التعاون بين القطاعين العام والخاص",
        "2. الاستثمار في التقنيات الحديثة والأتمتة",
        "3. تطوير برامج تدريب الكوادر الوطنية",
        "4. دعم الابتكار والبحث والتطوير",
        "5. تبني معايير الاستدامة في التصنيع",
        
        "## الملتقى القادم",
        "أعلنت مجموعة الفنون عن إقامة الملتقى القادم في العام المقبل، مع توسيع نطاق المشاركة ليشمل دول الخليج العربي."
      ]
    },
    en: {
      title: "ALFUNOON Modern Industries Forum",
      content: [
        "ALFUNOON Industries Group held its annual forum bringing together elite experts and specialists in manufacturing and design fields to discuss the latest trends and challenges in the sector.",
        
        "## Forum Objectives",
        "The forum aimed to achieve several strategic objectives:",
        "- Exchange of expertise among industry specialists",
        "- Showcasing the latest technologies and innovations",
        "- Building new business partnerships",
        "- Discussing market challenges and solutions",
        "- Supporting national talents in the industrial sector",
        
        "## Keynote Speakers",
        "### Manufacturing Experts",
        "The forum featured elite local and international experts who shared their insights on:",
        "- The future of manufacturing industry in the Kingdom",
        "- Digital transformation in the manufacturing sector",
        "- International quality standards",
        
        "### Entrepreneurs",
        "Several successful entrepreneurs shared their inspiring stories and experiences in:",
        "- Building successful industrial projects",
        "- Overcoming challenges and obstacles",
        "- Expansion and growth in local and regional markets",
        
        "## Workshops",
        "The forum included interactive workshops covering:",
        "- **Modern Design Workshop**: Reviewing the latest interior design trends",
        "- **Quality Management Workshop**: Implementing ISO standards in factories",
        "- **Digital Marketing Workshop**: Marketing strategies for industrial products",
        "- **Occupational Safety Workshop**: Best practices in the workplace",
        
        "## Accompanying Exhibition",
        "An exhibition was held alongside the forum where participating companies displayed:",
        "- Their latest products and innovations",
        "- Samples of completed projects",
        "- Modern technologies and equipment",
        
        "## Forum Recommendations",
        "The forum concluded with several important recommendations:",
        "1. Strengthening cooperation between public and private sectors",
        "2. Investing in modern technologies and automation",
        "3. Developing national workforce training programs",
        "4. Supporting innovation, research, and development",
        "5. Adopting sustainability standards in manufacturing",
        
        "## Next Forum",
        "ALFUNOON Group announced the next forum will be held next year, with expanded participation to include Gulf Cooperation Council countries."
      ]
    }
  },
  6: {
    ar: {
      title: "أساليب التصميم الداخلي المعاصر",
      content: [
        "يشهد عالم التصميم الداخلي تحولات جذرية تواكب التطورات التقنية وتغير أنماط الحياة. في هذا المقال، نستعرض أبرز أساليب التصميم الداخلي المعاصر واستخدام المواد المختلفة فيها.",
        
        "## الأساليب الرائجة في التصميم الداخلي",
        "### 1. الأسلوب المينيمالي (Minimalist)",
        "يتميز هذا الأسلوب بـ:",
        "- البساطة والخطوط النظيفة",
        "- الألوان المحايدة والفاتحة",
        "- تقليل العناصر الزخرفية",
        "- التركيز على الوظيفة والمساحة",
        
        "### 2. الأسلوب الصناعي (Industrial)",
        "مستوحى من المصانع والمستودعات القديمة:",
        "- إظهار المواد الخام كالطوب والحديد",
        "- الألوان الترابية والرمادية",
        "- الإضاءة المعدنية المكشوفة",
        "- الأسقف العالية والمساحات المفتوحة",
        
        "### 3. الأسلوب الاسكندنافي (Scandinavian)",
        "يجمع بين البساطة والدفء:",
        "- الأخشاب الفاتحة والطبيعية",
        "- الإضاءة الطبيعية الوفيرة",
        "- الألوان البيضاء والباستيل",
        "- النباتات والعناصر الطبيعية",
        
        "### 4. الأسلوب المعاصر الفاخر (Contemporary Luxury)",
        "يمزج بين الحداثة والفخامة:",
        "- المواد الفاخرة كالرخام والذهب",
        "- التشطيبات عالية الجودة",
        "- الإضاءة الدرامية",
        "- القطع الفنية المميزة",
        
        "## دور المواد في التصميم الداخلي",
        "### الخشب",
        "يضفي دفئاً وأصالة على المكان:",
        "- الأرضيات والأسقف الخشبية",
        "- الأثاث والتفاصيل الزخرفية",
        "- الكسوات الجدارية",
        
        "### الألمنيوم والمعادن",
        "تضيف لمسة عصرية وأنيقة:",
        "- النوافذ والأبواب",
        "- الفواصل والدرابزينات",
        "- عناصر الإضاءة والتجهيزات",
        
        "### الزجاج",
        "يوفر الشفافية والإضاءة الطبيعية:",
        "- الفواصل الزجاجية",
        "- الطاولات والأرفف",
        "- المرايا وعناصر الديكور",
        
        "### الرخام والحجر",
        "يمنح فخامة ورقياً:",
        "- الأرضيات والجدران",
        "- أسطح المطابخ والحمامات",
        "- التفاصيل الزخرفية",
        
        "### الستانلس ستيل",
        "يوفر أناقة عصرية ومتانة:",
        "- المطابخ التجارية والمنزلية",
        "- الدرابزينات والسلالم",
        "- عناصر الديكور الحديثة",
        
        "## اتجاهات التصميم الحديثة",
        "- **المساحات المفتوحة**: دمج المطبخ مع غرفة المعيشة",
        "- **الإضاءة الذكية**: التحكم في الأجواء بلمسة زر",
        "- **الاستدامة**: استخدام مواد صديقة للبيئة",
        "- **التقنية المدمجة**: إخفاء الأجهزة الإلكترونية",
        "- **العودة للطبيعة**: دمج النباتات والعناصر الطبيعية",
        
        "## خدمات مجموعة الفنون",
        "نقدم في مجموعة الفنون حلولاً متكاملة للتصميم الداخلي تشمل جميع المواد والتشطيبات. فريقنا من المصممين والمهندسين جاهز لتحويل أفكاركم إلى واقع يجمع بين الجمال والوظيفة."
      ]
    },
    en: {
      title: "Contemporary Interior Design Styles",
      content: [
        "The world of interior design is witnessing radical transformations keeping pace with technological developments and changing lifestyles. In this article, we explore the most prominent contemporary interior design styles and the use of various materials in them.",
        
        "## Popular Interior Design Styles",
        "### 1. Minimalist Style",
        "This style is characterized by:",
        "- Simplicity and clean lines",
        "- Neutral and light colors",
        "- Reduced decorative elements",
        "- Focus on function and space",
        
        "### 2. Industrial Style",
        "Inspired by old factories and warehouses:",
        "- Exposed raw materials like brick and iron",
        "- Earthy and gray colors",
        "- Exposed metal lighting",
        "- High ceilings and open spaces",
        
        "### 3. Scandinavian Style",
        "Combines simplicity and warmth:",
        "- Light and natural woods",
        "- Abundant natural lighting",
        "- White and pastel colors",
        "- Plants and natural elements",
        
        "### 4. Contemporary Luxury Style",
        "Blends modernity with luxury:",
        "- Luxurious materials like marble and gold",
        "- High-quality finishes",
        "- Dramatic lighting",
        "- Distinctive art pieces",
        
        "## The Role of Materials in Interior Design",
        "### Wood",
        "Adds warmth and authenticity to the space:",
        "- Wooden floors and ceilings",
        "- Furniture and decorative details",
        "- Wall claddings",
        
        "### Aluminum and Metals",
        "Add a modern and elegant touch:",
        "- Windows and doors",
        "- Partitions and railings",
        "- Lighting elements and fixtures",
        
        "### Glass",
        "Provides transparency and natural lighting:",
        "- Glass partitions",
        "- Tables and shelves",
        "- Mirrors and decorative elements",
        
        "### Marble and Stone",
        "Grants luxury and sophistication:",
        "- Floors and walls",
        "- Kitchen and bathroom countertops",
        "- Decorative details",
        
        "### Stainless Steel",
        "Provides modern elegance and durability:",
        "- Commercial and residential kitchens",
        "- Railings and stairs",
        "- Modern decorative elements",
        
        "## Modern Design Trends",
        "- **Open Spaces**: Merging kitchen with living room",
        "- **Smart Lighting**: Controlling ambiance at the touch of a button",
        "- **Sustainability**: Using eco-friendly materials",
        "- **Integrated Technology**: Hiding electronic devices",
        "- **Return to Nature**: Incorporating plants and natural elements",
        
        "## ALFUNOON Group Services",
        "At ALFUNOON Group, we provide integrated interior design solutions covering all materials and finishes. Our team of designers and engineers is ready to transform your ideas into reality that combines beauty and function."
      ]
    }
  }
};

const ArticleDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { language, isRTL } = useLanguage();
  const articleId = parseInt(id || "0");
  
  const article = articlesContent[articleId];
  
  if (!article) {
    return (
      <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            {language === 'ar' ? 'المقال غير موجود' : 'Article Not Found'}
          </h1>
          <Button asChild>
            <Link to="/articles">
              {language === 'ar' ? 'العودة للمقالات' : 'Back to Articles'}
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const t = article[language];

  const renderContent = (content: string[]) => {
    return content.map((paragraph, index) => {
      if (paragraph.startsWith("## ")) {
        return (
          <h2 key={index} className="text-2xl font-bold text-primary mt-8 mb-4 font-kufi">
            {paragraph.replace("## ", "")}
          </h2>
        );
      }
      if (paragraph.startsWith("### ")) {
        return (
          <h3 key={index} className="text-xl font-bold text-foreground mt-6 mb-3 font-kufi">
            {paragraph.replace("### ", "")}
          </h3>
        );
      }
      if (paragraph.startsWith("- ")) {
        return (
          <li key={index} className="text-muted-foreground font-kufi leading-relaxed mb-2">
            {paragraph.replace("- ", "").split("**").map((part, i) => 
              i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
            )}
          </li>
        );
      }
      if (paragraph.match(/^\d+\./)) {
        return (
          <li key={index} className="text-muted-foreground font-kufi leading-relaxed mb-2 list-decimal">
            {paragraph.replace(/^\d+\.\s*/, "").split("**").map((part, i) => 
              i % 2 === 1 ? <strong key={i} className="text-foreground">{part}</strong> : part
            )}
          </li>
        );
      }
      return (
        <p key={index} className="text-muted-foreground font-kufi leading-relaxed mb-4">
          {paragraph}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          {/* Back Button */}
          <Button variant="ghost" asChild className="mb-6">
            <Link to="/articles" className="flex items-center gap-2">
              <ArrowRight className={`h-4 w-4 ${isRTL ? '' : 'rotate-180'}`} />
              {language === 'ar' ? 'العودة للمقالات' : 'Back to Articles'}
            </Link>
          </Button>

          {/* Article Header */}
          <div className="bg-card rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-primary/10 p-8">
              <div className="flex items-center gap-2 text-primary mb-4">
                <BookOpen className="h-5 w-5" />
                <span className="font-kufi text-sm">
                  {language === 'ar' ? 'مقال تعليمي' : 'Educational Article'}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground font-kufi mb-4">
                {t.title}
              </h1>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span className="font-kufi text-sm">2023-07-18</span>
              </div>
            </div>

            {/* Article Content */}
            <div className="p-8">
              <ul className="list-none">
                {renderContent(t.content)}
              </ul>
            </div>
          </div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 bg-primary/10 rounded-2xl p-8 text-center"
          >
            <h3 className="text-2xl font-bold text-foreground font-kufi mb-4">
              {language === 'ar' ? 'هل تحتاج إلى زجاج معالج حرارياً؟' : 'Need Heat-Treated Glass?'}
            </h3>
            <p className="text-muted-foreground font-kufi mb-6">
              {language === 'ar' 
                ? 'تواصل معنا اليوم للحصول على استشارة مجانية وعرض سعر مخصص لمشروعك'
                : 'Contact us today for a free consultation and custom quote for your project'}
            </p>
            <Button size="lg" asChild>
              <Link to="/contact">
                {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default ArticleDetail;
